import React from 'react';
import CloseButton from './ui/CloseButton';

/**
 * Generic delete-confirm modal.
 *
 * Props:
 *  - open:        boolean — render or not.
 *  - title:       string — modal header title.
 *  - message:     string — body text. Optional when `references` is provided.
 *  - references:  array of { label, question_count, quiz_count } — optional. When
 *                 provided, the body renders a per-item list with status; if any item is
 *                 referenced by a question or quiz the Confirm button is disabled and a
 *                 warning alert is shown.
 *  - onConfirm:   () => void
 *  - onCancel:    () => void  — called by Cancel button and close-X.
 *  - busy:        boolean — disables both buttons while a delete is in flight.
 */
export default function ConfirmDeleteDialog({
    open,
    title,
    message,
    references,
    onConfirm,
    onCancel,
    busy,
}) {
    if (!open) return null;
    const refCount = (r) => (r.question_count || 0) + (r.quiz_count || 0);
    const blocked = (references || []).some(r => refCount(r) > 0);
    return (
        <div
            className="modal d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{title || 'Confirm delete'}</h5>
                        <CloseButton onClick={onCancel} disabled={busy} />
                    </div>
                    <div className="modal-body">
                        {message && <p>{message}</p>}
                        {references && references.length > 0 && (
                            <ul className="mb-0 ps-3">
                                {references.map(r => (
                                    <li
                                        key={r.label}
                                        className={refCount(r) > 0 ? 'text-danger' : 'text-muted'}
                                    >
                                        <strong>{r.label}</strong>
                                        {' — '}
                                        {refCount(r) > 0
                                            ? `used by ${r.question_count || 0} question(s), ${r.quiz_count || 0} quiz(zes)`
                                            : 'no references'}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {blocked && (
                            <div className="alert alert-warning mt-3 mb-0">
                                Some tags are still in use. Delete or re-tag the questions and
                                tests referencing these tags first, then retry the tag delete.
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onCancel}
                            disabled={busy}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={onConfirm}
                            disabled={busy || blocked}
                        >
                            {busy ? 'Deleting…' : 'Confirm'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
